import {
  CREATIVE_TEMPLATE_ARTBOARD_PRESETS,
  CREATIVE_TEMPLATE_DEFAULT_ARTBOARD_ID,
  CREATIVE_TEMPLATE_DEFAULT_ARTBOARD_SIZE,
} from "../../../utils/constants";
import {
  applyCanvasBackgroundLayout,
  countNodes,
  isCanvasBackgroundNode,
  setRootCanvasBackground,
} from "./layerTree";

const PRESET_ORDER = ["square", "portrait", "story", "landscape"];
const RATIO_MATCH_THRESHOLD = 0.08;

export const listArtboardIDs = ({ artboards = {} }) => {
  const ids = Object.keys(artboards || {});
  const rank = (id) => {
    if (id === CREATIVE_TEMPLATE_DEFAULT_ARTBOARD_ID) return 0;
    const presetIndex = PRESET_ORDER.indexOf(id);
    if (presetIndex >= 0) return 1 + presetIndex;
    return 10;
  };
  return ids.sort((left, right) => {
    const rankDiff = rank(left) - rank(right);
    if (rankDiff !== 0) return rankDiff;
    return left.localeCompare(right);
  });
};

export const getArtboard = ({ artboards = {}, artboardID }) =>
  artboards?.[artboardID] || null;

export const resolveActiveArtboardID = ({
  artboards = {},
  activeArtboardID,
}) => {
  if (activeArtboardID && artboards[activeArtboardID]) return activeArtboardID;
  if (artboards[CREATIVE_TEMPLATE_DEFAULT_ARTBOARD_ID]) {
    return CREATIVE_TEMPLATE_DEFAULT_ARTBOARD_ID;
  }
  return listArtboardIDs({ artboards })[0] || CREATIVE_TEMPLATE_DEFAULT_ARTBOARD_ID;
};

export const getArtboardRatio = ({ width, height }) => {
  if (!width || !height) return 1;
  return width / height;
};

export const matchPresetID = ({ width, height }) => {
  const ratio = getArtboardRatio({ width, height });
  let bestID = null;
  let bestDiff = Infinity;
  Object.values(CREATIVE_TEMPLATE_ARTBOARD_PRESETS).forEach((preset) => {
    const diff = Math.abs(ratio - preset.width / preset.height);
    if (diff < bestDiff) {
      bestDiff = diff;
      bestID = preset.id;
    }
  });
  if (bestDiff > RATIO_MATCH_THRESHOLD) return null;
  return bestID;
};

export const resolveArtboardSizeFromDimensions = ({ width, height } = {}) => {
  const presetID = matchPresetID({ width, height });
  if (presetID && CREATIVE_TEMPLATE_ARTBOARD_PRESETS[presetID]) {
    const preset = CREATIVE_TEMPLATE_ARTBOARD_PRESETS[presetID];
    return { width: preset.width, height: preset.height };
  }
  return {
    width: CREATIVE_TEMPLATE_DEFAULT_ARTBOARD_SIZE,
    height: CREATIVE_TEMPLATE_DEFAULT_ARTBOARD_SIZE,
  };
};

export const getArtboardLabel = ({ artboard, artboardID } = {}) => {
  if (artboard?.label && artboard.label !== "source") return artboard.label;
  if (artboardID && CREATIVE_TEMPLATE_ARTBOARD_PRESETS[artboardID]) {
    return CREATIVE_TEMPLATE_ARTBOARD_PRESETS[artboardID].label;
  }
  const presetID = matchPresetID(artboard || {});
  if (presetID) return CREATIVE_TEMPLATE_ARTBOARD_PRESETS[presetID].label;
  if (artboard?.width && artboard?.height) {
    return `${artboard.width}×${artboard.height}`;
  }
  return artboardID || CREATIVE_TEMPLATE_DEFAULT_ARTBOARD_ID;
};

export const getArtboardChipLabel = ({ artboardID, cardArtboard }) => {
  if (CREATIVE_TEMPLATE_ARTBOARD_PRESETS[artboardID]) {
    return CREATIVE_TEMPLATE_ARTBOARD_PRESETS[artboardID].label;
  }
  if (artboardID === CREATIVE_TEMPLATE_DEFAULT_ARTBOARD_ID && cardArtboard) {
    return getArtboardLabel({ artboard: cardArtboard, artboardID });
  }
  return getArtboardLabel({ artboardID });
};

export const listAvailablePresets = ({ artboards = {} }) => {
  const usedPresetIDs = new Set();
  Object.entries(artboards || {}).forEach(([id, artboard]) => {
    if (CREATIVE_TEMPLATE_ARTBOARD_PRESETS[id]) usedPresetIDs.add(id);
    const matched = matchPresetID(artboard || {});
    if (matched) usedPresetIDs.add(matched);
  });
  return PRESET_ORDER.map(
    (id) => CREATIVE_TEMPLATE_ARTBOARD_PRESETS[id]
  ).filter((preset) => preset && !usedPresetIDs.has(preset.id));
};

const cloneTree = (value) => structuredClone(value ?? {});

const scaleLayout = ({ layout, from, to }) => {
  if (!layout) return layout;
  const fromW = from?.width || 1;
  const fromH = from?.height || 1;
  const toW = to?.width || 1;
  const toH = to?.height || 1;
  return {
    ...layout,
    x: Math.round(((layout.x || 0) / fromW) * toW),
    y: Math.round(((layout.y || 0) / fromH) * toH),
    width: Math.round(((layout.width || 0) / fromW) * toW),
    height: Math.round(((layout.height || 0) / fromH) * toH),
  };
};

export const scaleNodeLayouts = ({ nodeLayouts = {}, from, to }) => {
  const next = {};
  Object.entries(nodeLayouts || {}).forEach(([id, layout]) => {
    next[id] = scaleLayout({ layout, from, to });
  });
  return next;
};

export const scaleNodesTree = ({ nodes = {}, from, to }) => {
  const next = {};
  Object.entries(nodes || {}).forEach(([id, node]) => {
    if (!node) return;
    const scaled = { ...node };
    if (node.layout) {
      scaled.layout = scaleLayout({ layout: node.layout, from, to });
    } else {
      delete scaled.layout;
    }
    if (node.children) {
      scaled.children = scaleNodesTree({
        nodes: node.children,
        from,
        to,
      });
    } else {
      delete scaled.children;
    }
    next[id] = scaled;
  });
  return next;
};

export const getArtboardNodes = ({ artboard, fallbackNodes = {} }) =>
  artboard?.nodes != null ? artboard.nodes : fallbackNodes;

export const hydrateArtboards = ({ artboards = {}, nodes: sharedNodes = {} }) => {
  const next = {};
  Object.entries(artboards || {}).forEach(([id, artboard]) => {
    next[id] = {
      ...artboard,
      nodes: getArtboardNodes({ artboard, fallbackNodes: cloneTree(sharedNodes) }),
      nodeLayouts: artboard?.nodeLayouts || {},
    };
  });
  return next;
};

export const seedArtboard = ({ fromArtboard, preset }) => {
  const nodes = scaleNodesTree({
    nodes: cloneTree(fromArtboard?.nodes || {}),
    from: fromArtboard,
    to: preset,
  });
  let nodeLayouts = scaleNodeLayouts({
    nodeLayouts: fromArtboard?.nodeLayouts || {},
    from: fromArtboard,
    to: preset,
  });
  Object.values(nodes).forEach((node) => {
    if (!isCanvasBackgroundNode({ node, parentID: null })) return;
    nodeLayouts = applyCanvasBackgroundLayout({
      nodeLayouts,
      nodeID: node.id,
      width: preset.width,
      height: preset.height,
    });
  });
  return {
    label: preset.label,
    width: preset.width,
    height: preset.height,
    nodes,
    nodeLayouts,
  };
};

export const applyCanvasBackgroundToArtboards = ({
  artboards = {},
  nodeID,
  isBackground,
}) => {
  const next = {};
  Object.entries(artboards).forEach(([artboardID, artboard]) => {
    const nodes = artboard?.nodes || {};
    if (!nodes[nodeID]) {
      next[artboardID] = artboard;
      return;
    }
    const nextNodes = setRootCanvasBackground({
      nodes,
      nodeID,
      isBackground,
    });
    const nextNode = nextNodes[nodeID];
    const shouldFillLayout =
      isBackground &&
      isCanvasBackgroundNode({ node: nextNode, parentID: null });
    next[artboardID] = {
      ...artboard,
      nodes: nextNodes,
      nodeLayouts: shouldFillLayout
        ? applyCanvasBackgroundLayout({
            nodeLayouts: artboard.nodeLayouts || {},
            nodeID,
            width: artboard.width,
            height: artboard.height,
          })
        : artboard.nodeLayouts,
    };
  });
  return next;
};

export const patchActiveArtboardGraph = ({
  artboards = {},
  artboardID,
  nodes,
  nodeLayouts,
}) => {
  const current = artboards[artboardID] || {};
  return {
    ...artboards,
    [artboardID]: {
      ...current,
      ...(nodes !== undefined ? { nodes } : {}),
      ...(nodeLayouts !== undefined ? { nodeLayouts } : {}),
    },
  };
};

export const countAllArtboardNodes = ({ artboards = {}, nodes } = {}) => {
  const fromArtboards = Object.values(artboards || {}).reduce(
    (total, artboard) => total + countNodes({ nodes: artboard?.nodes || {} }),
    0
  );
  if (fromArtboards > 0) return fromArtboards;
  return countNodes({ nodes: nodes || {} });
};

export const getReferenceAssetKey = ({ artboardID }) =>
  artboardID === CREATIVE_TEMPLATE_DEFAULT_ARTBOARD_ID
    ? "reference"
    : `reference_${artboardID}`;

export const getArtboardReference = ({
  artboard,
  artboardID,
  assets = {},
}) => {
  const refKey = artboard?.referenceAssetRef;
  if (refKey && assets[refKey]) return assets[refKey];
  if (
    !refKey &&
    artboardID === CREATIVE_TEMPLATE_DEFAULT_ARTBOARD_ID &&
    assets.reference
  ) {
    return assets.reference;
  }
  return null;
};

export const shouldShowReferenceBackdrop = ({
  artboard,
  artboardID,
  assets = {},
  reference,
}) =>
  Boolean(
    getArtboardReference({ artboard, artboardID, assets })?.url ||
      (reference?.url &&
        Number(artboard?.width) === Number(reference.width) &&
        Number(artboard?.height) === Number(reference.height))
  );

export const listBackdropAssets = ({ assets = {}, artboards = {} }) => {
  const keys = new Set();
  Object.keys(assets || {}).forEach((key) => {
    if (key === "reference" || key.startsWith("reference_")) keys.add(key);
  });
  Object.values(artboards || {}).forEach((artboard) => {
    if (artboard?.referenceAssetRef) keys.add(artboard.referenceAssetRef);
  });
  return [...keys]
    .map((key) => ({ key, asset: assets[key] }))
    .filter((entry) => entry.asset?.url);
};

export const findReferenceArtboardID = ({ artboards = {}, reference, assets = {} }) => {
  const matchedByRef = Object.entries(artboards).find(([, artboard]) => {
    const refKey = artboard?.referenceAssetRef;
    return refKey && assets[refKey]?.assetID && assets[refKey].assetID === reference?.assetID;
  });
  if (matchedByRef) return matchedByRef[0];
  const matched = Object.entries(artboards).find(([id, artboard]) =>
    shouldShowReferenceBackdrop({
      artboard,
      artboardID: id,
      assets,
      reference,
    })
  );
  if (matched) return matched[0];
  return resolveActiveArtboardID({
    artboards,
    activeArtboardID: CREATIVE_TEMPLATE_DEFAULT_ARTBOARD_ID,
  });
};

export const getCardArtboardFields = ({ artboards = {}, activeArtboardID }) => {
  const resolvedID = resolveActiveArtboardID({ artboards, activeArtboardID });
  const artboard = artboards[resolvedID] || {};
  return {
    artboard: {
      id: resolvedID,
      width: artboard.width || CREATIVE_TEMPLATE_DEFAULT_ARTBOARD_SIZE,
      height: artboard.height || CREATIVE_TEMPLATE_DEFAULT_ARTBOARD_SIZE,
    },
    artboardIDs: listArtboardIDs({ artboards }),
  };
};
